import { useState } from "react";
import {
  AlignCenter,
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignHorizontalDistributeCenter,
  AlignLeft,
  AlignRight,
  AlignStartHorizontal,
  AlignStartVertical,
  AlignVerticalDistributeCenter,
  BringToFront,
  Copy,
  Crop,
  FlipHorizontal2,
  FlipVertical2,
  LayoutGrid,
  LayoutTemplate,
  Lock,
  LockOpen,
  Palette,
  RotateCcw,
  RotateCw,
  SendToBack,
  Trash2,
  WandSparkles,
} from "lucide-react";
import { absoluteUrl } from "../api/client";
import { VECTOR_TYPES } from "./BoardCanvas";
import ColorPicker from "./ColorPicker";
import DrawOptions from "./DrawOptions";
import { ADJUSTMENTS, MAX_TILT, canRotate, canTilt, imageFilter, isAdjusted, normaliseAngle } from "./geometry";
import { arrowHeads } from "./vector";
import { AspectPicker } from "./TemplatePicker";
import { BACKGROUNDS } from "./templates";

const TEXT_COLOURS = ["#1e1b16", "#ffffff", "#8a5a3c", "#6b5326"];
const LINE_COLOURS = ["#a8563c", "#1e1b16", "#ffffff", "#2f6fd6", "#3f6742"];
const FOCUS_MAX_HEIGHT = 200;
const TITLES = { image: "Image", text: "Text", swatch: "Colour swatch", draw: "Drawing", arrow: "Arrow", shape: "Shape" };
const DRAW_KIND = { draw: "pencil", shape: "shape", arrow: "arrow" };
const NEUTRAL = { brightness: 100, contrast: 100, saturation: 100, sepia: 0 };
const LOOKS = [
  { key: "original", label: "Original", values: NEUTRAL },
  { key: "mono", label: "B&W", values: { brightness: 100, contrast: 110, saturation: 0, sepia: 0 } },
  { key: "sepia", label: "Sepia", values: { brightness: 100, contrast: 100, saturation: 100, sepia: 80 } },
  { key: "vivid", label: "Vivid", values: { brightness: 105, contrast: 115, saturation: 140, sepia: 0 } },
  { key: "soft", label: "Soft", values: { brightness: 108, contrast: 88, saturation: 85, sepia: 0 } },
  { key: "warm", label: "Warm", values: { brightness: 103, contrast: 100, saturation: 110, sepia: 30 } },
  { key: "fade", label: "Fade", values: { brightness: 110, contrast: 80, saturation: 70, sepia: 10 } },
];

// Arrows saved before per-end heads existed only have a single "head" field.
const legacyHeads = (item) => {
  const { start, end } = arrowHeads(item);
  return { head_start: start, head_end: end };
};

function Field({ label, children }) {
  return (
    <div className="mb-field">
      <span className="mb-field-label">{label}</span>
      {children}
    </div>
  );
}

function Segmented({ value, options, onChange, label }) {
  return (
    <div className="mb-segmented" role="radiogroup" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          aria-label={option.title || option.label}
          title={option.title}
          className={value === option.value ? "active" : ""}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function Slider({ label, value, min, max, step, format = (v) => v, onChange }) {
  return (
    <Field label={`${label} · ${format(value)}`}>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-label={label}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </Field>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <label className="mb-toggle">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

function FocusPicker({ item, asset, onChange }) {
  const [ratio, setRatio] = useState(null);
  const knownRatio = asset?.width && asset?.height ? asset.width / asset.height : null;
  const shape = ratio || knownRatio || 1.5;

  if (!asset?.url) return null;
  const setFocus = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const clampPct = (value) => Math.round(Math.min(100, Math.max(0, value)));
    onChange({
      focus_x: clampPct(((event.clientX - rect.left) / rect.width) * 100),
      focus_y: clampPct(((event.clientY - rect.top) / rect.height) * 100),
    });
  };
  return (
    <Field label="Focus point (click the part to keep in view)">
      {/* Sized to the image's own shape, so a click maps to the same spot on the picture. */}
      <div
        className="mb-focus is-active"
        style={{ aspectRatio: `${shape}`, width: `min(100%, ${Math.round(FOCUS_MAX_HEIGHT * shape)}px)` }}
        onClick={setFocus}
      >
        <img
          src={absoluteUrl(asset.url)}
          alt=""
          draggable={false}
          onLoad={(event) => {
            const { naturalWidth, naturalHeight } = event.currentTarget;
            if (naturalWidth && naturalHeight) setRatio(naturalWidth / naturalHeight);
          }}
        />
        <span className="mb-focus-dot" style={{ left: `${item.focus_x}%`, top: `${item.focus_y}%` }} />
      </div>
    </Field>
  );
}

function TransformControls({ item, change }) {
  const rotation = item.rotation || 0;
  const turnBy = (delta) => change({ rotation: normaliseAngle(rotation + delta) });
  // Drawings flip by mirroring their points; images keep a flip flag.
  const flip = (axis) =>
    item.type === "image"
      ? change({ [`flip_${axis}`]: !item[`flip_${axis}`] })
      : change({ points: item.points.map(([x, y]) => (axis === "x" ? [1 - x, y] : [x, 1 - y])) });
  const canFlip = item.type === "image" || item.type === "draw";

  return (
    <>
      <Slider
        label="Rotation"
        value={rotation}
        min={-180}
        max={180}
        step={1}
        format={(v) => `${Math.round(v)}°`}
        onChange={(value) => change({ rotation: value }, { merge: `rotation:${item.id}` })}
      />
      <div className="mb-transform">
        <button type="button" onClick={() => turnBy(-90)} title="Rotate 90° left" aria-label="Rotate 90 degrees left">
          <RotateCcw size={15} strokeWidth={1.8} />
        </button>
        <button type="button" onClick={() => turnBy(90)} title="Rotate 90° right" aria-label="Rotate 90 degrees right">
          <RotateCw size={15} strokeWidth={1.8} />
        </button>
        {canFlip && (
          <>
            <button
              type="button"
              className={item.flip_x ? "active" : ""}
              aria-pressed={item.type === "image" ? Boolean(item.flip_x) : undefined}
              onClick={() => flip("x")}
              title="Flip horizontally"
              aria-label="Flip horizontally"
            >
              <FlipHorizontal2 size={15} strokeWidth={1.8} />
            </button>
            <button
              type="button"
              className={item.flip_y ? "active" : ""}
              aria-pressed={item.type === "image" ? Boolean(item.flip_y) : undefined}
              onClick={() => flip("y")}
              title="Flip vertically"
              aria-label="Flip vertically"
            >
              <FlipVertical2 size={15} strokeWidth={1.8} />
            </button>
          </>
        )}
        <button type="button" className="mb-transform-reset" onClick={() => change({ rotation: 0 })} disabled={!rotation}>
          Straighten
        </button>
      </div>
    </>
  );
}

const SHADOW_OPTIONS = [
  { value: "none", label: "None" },
  { value: "soft", label: "Soft" },
  { value: "medium", label: "Medium" },
  { value: "strong", label: "Strong" },
];

// Drag the dot to tip the item back, forward, left or right; the card in the
// middle shows the tilt.
function TiltPad({ item, change }) {
  const tiltX = item.tilt_x || 0;
  const tiltY = item.tilt_y || 0;
  const set = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const unit = (value) => Math.max(-1, Math.min(1, value * 2 - 1));
    const across = unit((event.clientX - rect.left) / rect.width);
    const down = unit((event.clientY - rect.top) / rect.height);
    // Settle on flat near the middle, so it is easy to undo a tilt by hand.
    const snap = (value) => (Math.abs(value) < 2 ? 0 : value);
    change(
      { tilt_x: snap(Math.round(-down * MAX_TILT)), tilt_y: snap(Math.round(across * MAX_TILT)) },
      { merge: `tilt:${item.id}` }
    );
  };
  return (
    <div
      className="mb-tilt-pad"
      role="presentation"
      onPointerDown={(event) => {
        if (event.button !== 0) return;
        event.currentTarget.setPointerCapture(event.pointerId);
        set(event);
      }}
      onPointerMove={(event) => event.currentTarget.hasPointerCapture(event.pointerId) && set(event)}
      onDoubleClick={() => change({ tilt_x: 0, tilt_y: 0 })}
      title="Drag to tilt; double-click to lay flat"
    >
      <span
        className="mb-tilt-card"
        aria-hidden="true"
        style={{ transform: `perspective(120px) rotateX(${tiltX}deg) rotateY(${tiltY}deg)` }}
      />
      <span
        className="mb-tilt-dot"
        aria-hidden="true"
        style={{ left: `${50 + (tiltY / MAX_TILT) * 50}%`, top: `${50 - (tiltX / MAX_TILT) * 50}%` }}
      />
    </div>
  );
}

function DepthControls({ item, change }) {
  const tilted = Boolean(item.tilt_x || item.tilt_y);
  return (
    <>
      <div className="mb-tilt">
        <TiltPad item={item} change={change} />
        <div className="mb-tilt-sliders">
          <Slider
            label="Tip back"
            value={item.tilt_x || 0}
            min={-MAX_TILT}
            max={MAX_TILT}
            step={1}
            format={(v) => `${Math.round(v)}°`}
            onChange={(tilt_x) => change({ tilt_x }, { merge: `tilt:${item.id}` })}
          />
          <Slider
            label="Turn"
            value={item.tilt_y || 0}
            min={-MAX_TILT}
            max={MAX_TILT}
            step={1}
            format={(v) => `${Math.round(v)}°`}
            onChange={(tilt_y) => change({ tilt_y }, { merge: `tilt:${item.id}` })}
          />
          <button type="button" className="mb-text-button" onClick={() => change({ tilt_x: 0, tilt_y: 0 })} disabled={!tilted}>
            Lay flat
          </button>
        </div>
      </div>
      <Field label="Shadow">
        <Segmented label="Shadow" value={item.shadow || "none"} options={SHADOW_OPTIONS} onChange={(shadow) => change({ shadow })} />
      </Field>
    </>
  );
}

function Adjustments({ item, asset, change }) {
  const current = Object.fromEntries(ADJUSTMENTS.map(({ key, neutral }) => [key, item[key] ?? neutral]));
  const activeLook = LOOKS.find((look) => ADJUSTMENTS.every(({ key }) => look.values[key] === current[key]))?.key;
  return (
    <>
      {asset?.url && (
        <div className="mb-looks" role="radiogroup" aria-label="Looks">
          {LOOKS.map((look) => (
            <button
              key={look.key}
              type="button"
              role="radio"
              aria-checked={activeLook === look.key}
              className={activeLook === look.key ? "active" : ""}
              onClick={() => change({ ...look.values })}
            >
              <img src={absoluteUrl(asset.url)} alt="" draggable={false} style={{ filter: imageFilter(look.values) }} />
              <span>{look.label}</span>
            </button>
          ))}
        </div>
      )}
      {ADJUSTMENTS.map(({ key, label, min, max, neutral }) => (
        <Slider
          key={key}
          label={label}
          value={current[key]}
          min={min}
          max={max}
          step={1}
          format={(v) => (key === "sepia" ? `${v}%` : `${v - neutral > 0 ? "+" : ""}${v - neutral}`)}
          onChange={(value) => change({ [key]: value }, { merge: `${key}:${item.id}` })}
        />
      ))}
      {isAdjusted(item) && (
        <button type="button" className="mb-text-button" onClick={() => change({ ...NEUTRAL })}>
          Reset adjustments
        </button>
      )}
    </>
  );
}

function BoardSettings({ doc, palette, paletteBusy, onBoardChange, onChangeLayout, onAutoArrange, onExtractPalette, onAddSwatches }) {
  return (
    <>
      <h3>Board</h3>
      <section className="mb-section">
        <Field label="Size">
          <AspectPicker compact value={doc.aspect} onChange={(aspect) => onBoardChange({ aspect })} />
        </Field>
      </section>
      <section className="mb-section">
        <Field label="Background">
          <ColorPicker
            label="Background"
            value={doc.background}
            palettes={[
              { label: "Presets", colors: BACKGROUNDS },
              { label: "From images", colors: palette },
            ]}
            onChange={(background, continuous) =>
              onBoardChange({ background }, continuous ? { merge: "background" } : undefined)
            }
          />
        </Field>
      </section>
      <section className="mb-section">
        <Field label="Layout">
          <div className="mb-button-stack">
            <button type="button" className="mb-wide-button" onClick={onChangeLayout}>
              <LayoutTemplate size={15} strokeWidth={1.8} aria-hidden="true" />
              Change layout
            </button>
            <button type="button" className="mb-wide-button" onClick={onAutoArrange}>
              <LayoutGrid size={15} strokeWidth={1.8} aria-hidden="true" />
              Auto-arrange images
            </button>
          </div>
        </Field>
      </section>
      <section className="mb-section">
        <Field label="Colour palette">
          <button type="button" className="mb-wide-button" onClick={onExtractPalette} disabled={paletteBusy}>
            <Palette size={15} strokeWidth={1.8} aria-hidden="true" />
            {paletteBusy ? "Reading colours..." : "Extract colours"}
          </button>
          {palette.length > 0 ? (
            <div className="mb-palette-result">
              <div className="mb-palette-chips">
                {palette.map((colour) => (
                  <span key={colour} style={{ background: colour }} title={colour} />
                ))}
              </div>
              <button type="button" className="mb-text-button" onClick={onAddSwatches}>
                Add as swatches
              </button>
            </div>
          ) : (
            <p className="mb-muted">Reads the main colours of your images so you can add them as swatches.</p>
          )}
        </Field>
      </section>
      <div className="mb-help">
        <p>Drag images from the left onto a frame to replace it, or click one to add it.</p>
        <p>
          Draw with the pencil, shape and arrow tools; pick brush, shape or arrow style in the bar above the board.
          Hold Shift for straight arrows and even shapes.
        </p>
        <p>
          Shift-click or drag across empty space to select several items. Hold Alt while dragging to turn off
          snapping, or Shift while resizing to keep the shape.
        </p>
        <p>
          Drag the round handle above an item to rotate it. Double-click an image to crop it. Tilt images, text and
          swatches in 3D, and give them a shadow, from their settings.
        </p>
        <p>
          Shortcuts: <kbd>V</kbd> select, <kbd>P</kbd> pencil, <kbd>B</kbd> shape, <kbd>A</kbd> arrow,{" "}
          <kbd>Ctrl</kbd>+<kbd>Z</kbd> undo, <kbd>Ctrl</kbd>+<kbd>C</kbd>/<kbd>V</kbd> copy and paste,{" "}
          <kbd>Ctrl</kbd>+<kbd>D</kbd> duplicate, <kbd>Del</kbd> remove, arrow keys nudge.
        </p>
      </div>
    </>
  );
}

function ArrangeButtons({ items, onArrange, onDuplicate, onDelete, onLock }) {
  const allLocked = items.every((item) => item.locked);
  return (
    <div className="mb-arrange">
      {items.length === 1 && (
        <>
          <button type="button" onClick={() => onArrange(items[0].id, "front")} title="Bring to front">
            <BringToFront size={15} strokeWidth={1.8} />
            <span>Front</span>
          </button>
          <button type="button" onClick={() => onArrange(items[0].id, "back")} title="Send to back">
            <SendToBack size={15} strokeWidth={1.8} />
            <span>Back</span>
          </button>
        </>
      )}
      <button type="button" onClick={() => onLock(!allLocked)} title={allLocked ? "Unlock" : "Lock position"}>
        {allLocked ? <LockOpen size={15} strokeWidth={1.8} /> : <Lock size={15} strokeWidth={1.8} />}
        <span>{allLocked ? "Unlock" : "Lock"}</span>
      </button>
      <button type="button" onClick={onDuplicate} title="Duplicate (Ctrl+D)">
        <Copy size={15} strokeWidth={1.8} />
        <span>Copy</span>
      </button>
      <button type="button" className="danger" onClick={onDelete} title="Delete (Del)">
        <Trash2 size={15} strokeWidth={1.8} />
        <span>Delete</span>
      </button>
    </div>
  );
}

const ALIGN_BUTTONS = [
  ["left", AlignStartVertical, "Align left"],
  ["center", AlignCenterVertical, "Align centres horizontally"],
  ["right", AlignEndVertical, "Align right"],
  ["top", AlignStartHorizontal, "Align top"],
  ["middle", AlignCenterHorizontal, "Align middles vertically"],
  ["bottom", AlignEndHorizontal, "Align bottom"],
];

function AlignButtons({ count, onAlign, onDistribute }) {
  return (
    <Field label={count === 1 ? "Align to board" : "Align"}>
      <div className="mb-align">
        {ALIGN_BUTTONS.map(([mode, Icon, title]) => (
          <button key={mode} type="button" onClick={() => onAlign(mode)} title={title} aria-label={title}>
            <Icon size={16} strokeWidth={1.8} />
          </button>
        ))}
        {count >= 3 && (
          <>
            <button type="button" onClick={() => onDistribute("horizontal")} title="Space evenly across" aria-label="Space evenly across">
              <AlignHorizontalDistributeCenter size={16} strokeWidth={1.8} />
            </button>
            <button type="button" onClick={() => onDistribute("vertical")} title="Space evenly down" aria-label="Space evenly down">
              <AlignVerticalDistributeCenter size={16} strokeWidth={1.8} />
            </button>
          </>
        )}
      </div>
    </Field>
  );
}

export default function Inspector(props) {
  const {
    items,
    assets,
    boardColours,
    palette,
    bgAvailable,
    removingBackground,
    onItemChange,
    onItemsChange,
    onArrange,
    onAlign,
    onDistribute,
    onDuplicate,
    onDelete,
    onRemoveBackground,
    onOpenCrop,
  } = props;

  if (!items.length) {
    return (
      <aside className="mb-panel mb-inspector" aria-label="Board settings">
        <BoardSettings {...props} />
      </aside>
    );
  }

  const lock = (locked) => onItemsChange(items.map((entry) => entry.id), { locked });
  const colourRows = [
    { label: "On this board", colors: boardColours },
    { label: "From images", colors: palette },
  ];

  if (items.length > 1) {
    return (
      <aside className="mb-panel mb-inspector" aria-label="Selection settings">
        <h3>{items.length} items selected</h3>
        <section className="mb-section">
          <AlignButtons count={items.length} onAlign={onAlign} onDistribute={onDistribute} />
        </section>
        <section className="mb-section">
          <Field label="Arrange">
            <ArrangeButtons items={items} onArrange={onArrange} onDuplicate={onDuplicate} onDelete={onDelete} onLock={lock} />
          </Field>
          <p className="mb-muted">Drag any selected item to move them together.</p>
        </section>
      </aside>
    );
  }

  const item = items[0];
  const asset = assets[`${item.source}:${item.ref}`];
  const change = (patch, options) => onItemChange(item.id, patch, options);

  return (
    <aside className="mb-panel mb-inspector" aria-label={`${TITLES[item.type]} settings`}>
      <h3>
        {TITLES[item.type]}
        {item.locked && <span className="mb-locked-tag">Locked</span>}
      </h3>

      {item.type === "image" && (
        <section className="mb-section">
          <Field label="Fit">
            <Segmented
              label="Fit"
              value={item.fit}
              onChange={(fit) => change({ fit })}
              options={[
                { value: "cover", label: "Fill frame" },
                { value: "contain", label: "Whole image" },
              ]}
            />
          </Field>
          {item.source && asset?.url && (
            <div className="mb-button-pair">
              <button type="button" className="mb-wide-button" onClick={() => onOpenCrop(item.id)}>
                <Crop size={15} strokeWidth={1.8} aria-hidden="true" />
                {item.crop ? "Edit crop" : "Crop"}
              </button>
              {item.crop && (
                <button type="button" className="mb-wide-button" onClick={() => change({ crop: null })}>
                  Undo crop
                </button>
              )}
            </div>
          )}
          <FocusPicker key={asset?.url || "none"} item={item} asset={asset} onChange={change} />
          <Slider
            label="Zoom"
            value={item.zoom ?? 1}
            min={1}
            max={4}
            step={0.05}
            format={(v) => `${Math.round(v * 100)}%`}
            onChange={(zoom) => change({ zoom }, { merge: `zoom:${item.id}` })}
          />
          <Slider
            label="Corner radius"
            value={item.radius ?? 0}
            min={0}
            max={50}
            step={1}
            format={(v) => (v >= 50 ? "circle" : `${v}%`)}
            onChange={(radius) => change({ radius }, { merge: `radius:${item.id}` })}
          />
          <Slider
            label="Border"
            value={item.border_width ?? 0}
            min={0}
            max={3}
            step={0.1}
            format={(v) => (v ? v.toFixed(1) : "none")}
            onChange={(border_width) => change({ border_width }, { merge: `border:${item.id}` })}
          />
          {item.border_width > 0 && (
            <Field label="Border colour">
              <ColorPicker
                label="Border colour"
                value={item.border_color || "#ffffff"}
                palettes={colourRows}
                onChange={(border_color, continuous) =>
                  change({ border_color }, continuous ? { merge: `bcolor:${item.id}` } : undefined)
                }
              />
            </Field>
          )}
          {asset?.name && (
            <Toggle label="Show artwork name" checked={Boolean(item.caption)} onChange={(caption) => change({ caption })} />
          )}
          {item.source === "upload" && bgAvailable && (
            <button
              type="button"
              className="mb-wide-button"
              onClick={() => onRemoveBackground(item)}
              disabled={removingBackground}
            >
              <WandSparkles size={15} strokeWidth={1.8} aria-hidden="true" />
              {removingBackground ? "Removing background..." : "Remove background"}
            </button>
          )}
          {item.source ? (
            <button
              type="button"
              className="mb-wide-button"
              onClick={() => change({ source: null, ref: null, crop: null, focus_x: 50, focus_y: 50, zoom: 1 })}
            >
              Clear image
            </button>
          ) : (
            <p className="mb-muted">Drag an image here from the left, or click one while this frame is selected.</p>
          )}
        </section>
      )}

      {item.type === "text" && (
        <section className="mb-section">
          <Field label="Text">
            <textarea
              rows={4}
              value={item.text}
              maxLength={1000}
              onChange={(event) => change({ text: event.target.value }, { merge: `text:${item.id}` })}
            />
          </Field>
          <Field label="Font">
            <Segmented
              label="Font"
              value={item.font_family}
              onChange={(font_family) => change({ font_family })}
              options={[
                { value: "serif", label: "Serif" },
                { value: "sans", label: "Sans" },
              ]}
            />
          </Field>
          <Field label="Weight">
            <Segmented
              label="Weight"
              value={item.font_weight}
              onChange={(font_weight) => change({ font_weight })}
              options={[
                { value: "normal", label: "Regular" },
                { value: "bold", label: "Bold" },
              ]}
            />
          </Field>
          <Slider
            label="Size"
            value={item.font_size}
            min={0.8}
            max={12}
            step={0.1}
            onChange={(font_size) => change({ font_size }, { merge: `size:${item.id}` })}
          />
          <Field label="Align">
            <Segmented
              label="Align"
              value={item.align}
              onChange={(align) => change({ align })}
              options={[
                { value: "left", label: <AlignLeft size={15} />, title: "Left" },
                { value: "center", label: <AlignCenter size={15} />, title: "Centre" },
                { value: "right", label: <AlignRight size={15} />, title: "Right" },
              ]}
            />
          </Field>
          <Field label="Colour">
            <ColorPicker
              label="Text colour"
              value={item.color}
              palettes={[{ label: "Presets", colors: TEXT_COLOURS }, ...colourRows]}
              onChange={(color, continuous) => change({ color }, continuous ? { merge: `color:${item.id}` } : undefined)}
            />
          </Field>
        </section>
      )}

      {item.type === "swatch" && (
        <section className="mb-section">
          <Field label="Colour">
            <ColorPicker
              label="Swatch colour"
              value={item.color}
              palettes={colourRows}
              onChange={(color, continuous) => change({ color }, continuous ? { merge: `color:${item.id}` } : undefined)}
            />
          </Field>
          <Field label="Label">
            <input
              type="text"
              value={item.label}
              maxLength={40}
              placeholder="e.g. Terracotta"
              onChange={(event) => change({ label: event.target.value }, { merge: `label:${item.id}` })}
            />
          </Field>
          <Slider
            label="Corner radius"
            value={item.radius ?? 0}
            min={0}
            max={50}
            step={1}
            format={(v) => (v >= 50 ? "circle" : `${v}%`)}
            onChange={(radius) => change({ radius }, { merge: `radius:${item.id}` })}
          />
        </section>
      )}

      {item.type === "image" && item.source && (
        <section className="mb-section">
          <h4 className="mb-section-title">Adjust</h4>
          <Adjustments item={item} asset={asset} change={change} />
        </section>
      )}

      {VECTOR_TYPES.includes(item.type) && (
        <section className="mb-section">
          <DrawOptions
            kind={DRAW_KIND[item.type]}
            value={item.type === "arrow" ? { ...item, ...legacyHeads(item) } : item}
            onChange={change}
            palettes={[{ label: "Presets", colors: LINE_COLOURS }, ...colourRows]}
            mergeKey={item.id}
          />
        </section>
      )}

      {canRotate(item) && (
        <section className="mb-section">
          <h4 className="mb-section-title">Rotate{item.type === "image" || item.type === "draw" ? " & flip" : ""}</h4>
          <TransformControls item={item} change={change} />
        </section>
      )}

      {canTilt(item) && (
        <section className="mb-section">
          <h4 className="mb-section-title">3D tilt & shadow</h4>
          <DepthControls item={item} change={change} />
        </section>
      )}

      <section className="mb-section">
        <Slider
          label="Opacity"
          value={item.opacity ?? 1}
          min={0.05}
          max={1}
          step={0.05}
          format={(v) => `${Math.round(v * 100)}%`}
          onChange={(opacity) => change({ opacity }, { merge: `opacity:${item.id}` })}
        />
      </section>
      <section className="mb-section">
        <AlignButtons count={1} onAlign={onAlign} onDistribute={onDistribute} />
        <Field label="Arrange">
          <ArrangeButtons items={items} onArrange={onArrange} onDuplicate={onDuplicate} onDelete={onDelete} onLock={lock} />
        </Field>
      </section>
    </aside>
  );
}
